import { httpClient } from '@/shared/api';
import type {
  GovernorateBandCrossTabDto,
  GovernorateBandTableRow,
  GovernorateComparisonRow,
  RegionalBandCountDto,
  RegionalComparisonSchoolRowDto,
  RegionalComparisonsReportDto,
  RegionSchoolStatisticsDto,
} from './regionalComparisons.dto';

const PREFIX = '/app/regional-comparisons/regional-comparisons';

function readNum(o: Record<string, unknown>, keys: string[]): number {
  for (const k of keys) {
    const v = o[k];
    if (v == null || v === '') continue;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function readId(o: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = o[k];
    if (v == null) continue;
    if (typeof v === 'string' && v.trim() !== '') return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return null;
}

function readStr(o: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim() !== '') return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

function unwrapPayload(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if ('result' in o && o.result != null && typeof o.result === 'object') {
    return o.result as Record<string, unknown>;
  }
  if ('data' in o && o.data != null && typeof o.data === 'object') {
    return o.data as Record<string, unknown>;
  }
  return o;
}

function readSchoolList(o: Record<string, unknown>): unknown[] {
  const keys = [
    'regionalComparisonsDtos',
    'RegionalComparisonsDtos',
    'items',
    'Items',
  ];
  for (const k of keys) {
    const v = o[k];
    if (Array.isArray(v)) return v;
  }
  return [];
}

function parseBandList(raw: unknown, listKeys: string[]): RegionalBandCountDto[] {
  let arr: unknown[] = [];
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    for (const k of listKeys) {
      const v = o[k];
      if (Array.isArray(v)) {
        arr = v;
        break;
      }
    }
  }
  if (!arr.length && Array.isArray(raw)) arr = raw;

  return arr
    .map((item): RegionalBandCountDto | null => {
      if (!item || typeof item !== 'object') return null;
      const o = item as Record<string, unknown>;
      const name = readStr(o, ['name', 'Name']);
      if (!name) return null;
      return {
        name,
        numberOfSchools: readNum(o, ['numberOfSchools', 'NumberOfSchools']),
        numberOfStudents: readNum(o, ['numberOfStudents', 'NumberOfStudents']),
      };
    })
    .filter((x): x is RegionalBandCountDto => x != null)
    .sort((a, b) => b.numberOfSchools - a.numberOfSchools);
}

function readOptionalNum(o: Record<string, unknown>, keys: string[]): number | null {
  for (const k of keys) {
    const v = o[k];
    if (v == null) continue;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function parseRegionSchoolStatisticsList(raw: unknown, listKeys: string[]): RegionSchoolStatisticsDto[] {
  let arr: unknown[] = [];
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    for (const k of listKeys) {
      const v = o[k];
      if (Array.isArray(v)) {
        arr = v;
        break;
      }
    }
  }
  if (!arr.length && Array.isArray(raw)) arr = raw;

  return arr
    .map((item): RegionSchoolStatisticsDto | null => {
      if (!item || typeof item !== 'object') return null;
      const o = item as Record<string, unknown>;
      const govId = readOptionalNum(o, ['governorateId', 'GovernorateId']);
      const govNameRaw = readStr(o, ['governorateName', 'GovernorateName']);
      const totalSchools = readNum(o, ['totalSchools', 'TotalSchools']);
      const submittedCount = readNum(o, ['submittedSchoolsCount', 'SubmittedSchoolsCount']);
      const submittedRatio = readNum(o, ['submittedRatio', 'SubmittedRatio']);

      return {
        governorateId: govId,
        governorateName: govNameRaw || null,
        totalStudents: readNum(o, ['totalStudents', 'TotalStudents']),
        averageTuitionPerStudent: readNum(o, [
          'averageTuitionPerStudent',
          'AverageTuitionPerStudent',
        ]),
        minTuitionRate: readNum(o, ['minTuitionRate', 'MinTuitionRate']),
        maxTuitionRate: readNum(o, ['maxTuitionRate', 'MaxTuitionRate']),
        totalSchools,
        submittedSchoolsCount: submittedCount,
        submittedRatio,
      };
    })
    .filter((x): x is RegionSchoolStatisticsDto => x != null);
}

function submittedRatePct(s: RegionSchoolStatisticsDto): number {
  if (s.submittedRatio > 0 && s.submittedRatio <= 1) return s.submittedRatio * 100;
  if (s.totalSchools > 0) return (s.submittedSchoolsCount / s.totalSchools) * 100;
  return 0;
}

export function mapRegionSchoolStatisticsToRows(
  stats: RegionSchoolStatisticsDto[]
): GovernorateComparisonRow[] {
  return stats.map((s, index) => ({
    rowId: s.governorateId != null ? String(s.governorateId) : `unassigned-${index}`,
    governorateId: s.governorateId != null ? String(s.governorateId) : '',
    governorateName: s.governorateName?.trim() || 'Unassigned',
    schoolsCount: s.totalSchools,
    studentsCount: s.totalStudents,
    avgTuitionUsdWeighted: s.averageTuitionPerStudent,
    minTuitionUsd: s.minTuitionRate,
    maxTuitionUsd: s.maxTuitionRate,
    budgetSubmittedRatePct: submittedRatePct(s),
  }));
}

export function weightedNationalAvgTuition(stats: RegionSchoolStatisticsDto[]): number {
  let weightedSum = 0;
  let students = 0;
  for (const s of stats) {
    weightedSum += s.averageTuitionPerStudent * s.totalStudents;
    students += s.totalStudents;
  }
  return students > 0 ? weightedSum / students : 0;
}

function parseGovernorateBandCrossTabList(raw: unknown, listKeys: string[]): GovernorateBandCrossTabDto[] {
  let arr: unknown[] = [];
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    for (const k of listKeys) {
      const v = o[k];
      if (Array.isArray(v)) {
        arr = v;
        break;
      }
    }
  }
  if (!arr.length && Array.isArray(raw)) arr = raw;

  return arr
    .map((item): GovernorateBandCrossTabDto | null => {
      if (!item || typeof item !== 'object') return null;
      const o = item as Record<string, unknown>;
      const bandName = readStr(o, ['bandName', 'BandName']);
      if (!bandName) return null;
      return {
        governorateId: readOptionalNum(o, ['governorateId', 'GovernorateId']),
        governorateName: readStr(o, ['governorateName', 'GovernorateName']) || null,
        bandName,
        students: readNum(o, ['students', 'Students']),
        percentOfGovernorateStudents: readNum(o, [
          'percentOfGovernorateStudents',
          'PercentOfGovernorateStudents',
        ]),
        schools: readNum(o, ['schools', 'Schools']),
        avgTuitionUSD: readNum(o, ['avgTuitionUSD', 'AvgTuitionUSD', 'avgTuitionUsd']),
        submittedRate: readNum(o, ['submittedRate', 'SubmittedRate']),
        complianceRate: readNum(o, ['complianceRate', 'ComplianceRate']),
      };
    })
    .filter((x): x is GovernorateBandCrossTabDto => x != null);
}

function mapCrossTabToDetailRows(items: GovernorateBandCrossTabDto[]): GovernorateBandTableRow[] {
  return items.map((item, index) => ({
    rowId: `${item.governorateId ?? 'unassigned'}-${item.bandName}-${index}`,
    governorateId: item.governorateId != null ? String(item.governorateId) : '',
    governorateName:
      item.governorateName?.trim() && item.governorateName.trim() !== 'Unknown Governorate'
        ? item.governorateName.trim()
        : 'Unassigned',
    bandLabel: item.bandName,
    studentsCount: item.students,
    studentShareOfGovernoratePct: item.percentOfGovernorateStudents,
    schoolsCount: item.schools,
    avgTuitionUsdWeighted: item.avgTuitionUSD,
    budgetSubmittedRatePct: item.submittedRate,
    complianceRatePct: item.complianceRate,
    isSummary: false,
  }));
}

function buildGovernorateSummaryRow(
  governorateId: string,
  governorateName: string,
  details: GovernorateBandTableRow[]
): GovernorateBandTableRow {
  const students = details.reduce((sum, r) => sum + r.studentsCount, 0);
  const schools = details.reduce((sum, r) => sum + r.schoolsCount, 0);
  const pctSum = details.reduce((sum, r) => sum + r.studentShareOfGovernoratePct, 0);
  const avgTuition =
    students > 0
      ? details.reduce((sum, r) => sum + r.avgTuitionUsdWeighted * r.studentsCount, 0) / students
      : 0;
  const submitted =
    schools > 0
      ? details.reduce((sum, r) => sum + r.budgetSubmittedRatePct * r.schoolsCount, 0) / schools
      : 0;
  const compliance =
    schools > 0
      ? details.reduce((sum, r) => sum + r.complianceRatePct * r.schoolsCount, 0) / schools
      : 0;

  return {
    rowId: `${governorateId}-summary`,
    governorateId,
    governorateName,
    bandLabel: 'Governorate total',
    studentsCount: students,
    studentShareOfGovernoratePct: pctSum > 0 ? pctSum : students > 0 ? 100 : 0,
    schoolsCount: schools,
    avgTuitionUsdWeighted: avgTuition,
    budgetSubmittedRatePct: submitted,
    complianceRatePct: compliance,
    isSummary: true,
  };
}

/** Sort band detail rows within each governorate, then append a summary row per governorate. */
export function buildGovernorateBandTableRows(
  items: GovernorateBandCrossTabDto[],
  compareDetails: (a: GovernorateBandTableRow, b: GovernorateBandTableRow) => number
): GovernorateBandTableRow[] {
  const details = mapCrossTabToDetailRows(items);
  if (!details.length) return [];

  const byGovernorate = new Map<string, GovernorateBandTableRow[]>();
  for (const row of details) {
    const key = row.governorateId || row.governorateName;
    const list = byGovernorate.get(key) ?? [];
    list.push(row);
    byGovernorate.set(key, list);
  }

  const governorateOrder = [...byGovernorate.entries()].sort(([, aRows], [, bRows]) =>
    (aRows[0]?.governorateName ?? '').localeCompare(bRows[0]?.governorateName ?? '', undefined, {
      sensitivity: 'base',
    })
  );

  const out: GovernorateBandTableRow[] = [];
  for (const [, rows] of governorateOrder) {
    const sorted = [...rows].sort(compareDetails);
    out.push(...sorted);
    const first = sorted[0];
    if (first) {
      out.push(buildGovernorateSummaryRow(first.governorateId, first.governorateName, sorted));
    }
  }
  return out;
}

function parseSchoolRow(raw: unknown): RegionalComparisonSchoolRowDto | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const schoolId = readId(o, ['schoolId', 'SchoolId']);
  const schoolName = readStr(o, ['schoolName', 'SchoolName']);
  if (!schoolId || !schoolName) return null;

  const permTeacherCount = readNum(o, ['permTeacher_Count', 'permTeacherCount', 'PermTeacher_Count']);
  const contTeacherCount = readNum(o, ['contTeacher_Count', 'contTeacherCount', 'ContTeacher_Count']);
  const adminTeacherCount = readNum(o, ['adminTeacher_Count', 'adminTeacherCount', 'AdminTeacher_Count']);

  const permTeacherTotalSalary = readNum(o, [
    'permTeacher_TotalSalary',
    'permTeacherTotalSalary',
    'PermTeacher_TotalSalary',
  ]);
  const contTeacherTotalSalary = readNum(o, [
    'contTeacher_TotalSalary',
    'contTeacherTotalSalary',
    'ContTeacher_TotalSalary',
  ]);
  const adminTeacherTotalSalary = readNum(o, [
    'adminTeacher_TotalSalary',
    'adminTeacherTotalSalary',
    'AdminTeacher_TotalSalary',
  ]);

  return {
    budgetId: readId(o, ['budgetId', 'BudgetId']),
    schoolId,
    schoolName,
    exchangeRateAmount: readNum(o, ['exchangeRateAmount', 'ExchangeRateAmount']),
    studentsCount: readNum(o, ['studentsCount', 'StudentsCount']),
    scholarshipStudentsCount: readNum(o, ['scholarshipStudentsCount', 'ScholarshipStudentsCount']),
    permTeacherCount,
    permTeacherTotalSalary,
    contTeacherCount,
    contTeacherTotalSalary,
    adminTeacherCount,
    adminTeacherTotalSalary,
    expenseTotalAmountUsd: readNum(o, ['expense_TotalAmountUSD', 'expenseTotalAmountUSD', 'Expense_TotalAmountUSD']),
    expenseTotalAmountLl: readNum(o, ['expense_TotalAmountLL', 'expenseTotalAmountLL', 'Expense_TotalAmountLL']),
    totalRevenuesUsd: readNum(o, ['totalRevenuesTotaltUSD', 'totalRevenuesTotalUSD', 'TotalRevenuesTotaltUSD']),
    totalRevenuesLl: readNum(o, ['totalRevenuesTotaltLL', 'totalRevenuesTotalLL', 'TotalRevenuesTotaltLL']),
    totalTeacherCount: permTeacherCount + contTeacherCount + adminTeacherCount,
    totalTeacherSalaryUsd: permTeacherTotalSalary + contTeacherTotalSalary + adminTeacherTotalSalary,
  };
}

export function mapRegionalComparisonsReport(raw: unknown): RegionalComparisonsReportDto | null {
  const root = unwrapPayload(raw);
  if (!root) return null;

  const schools = readSchoolList(root)
    .map(parseSchoolRow)
    .filter((x): x is RegionalComparisonSchoolRowDto => x != null);

  return {
    totalStudentsCount: readNum(root, ['totalStudentsCount', 'TotalStudentsCount']),
    totalScholarshipStudentsCount: readNum(root, [
      'totalScholarshipStudentsCount',
      'TotalScholarshipStudentsCount',
    ]),
    totalTeacherCount: readNum(root, ['totalTeacherCount', 'TotalTeacherCount']),
    totalPermTeacherCount: readNum(root, [
      'totalPermTeacherCount',
      'TotalPermTeacherCount',
    ]),
    totalContTeacherCount: readNum(root, [
      'totalContTeacherCount',
      'TotalContTeacherCount',
    ]),
    totalAdminTeacherCount: readNum(root, [
      'totalAdminTeacherCount',
      'TotalAdminTeacherCount',
    ]),
    totalSalaries: readNum(root, ['totalSalaries', 'TotalSalaries']),
    totalPermSalaries: readNum(root, ['totalPermSalaries', 'TotalPermSalaries']),
    totalContSalaries: readNum(root, ['totalContSalaries', 'TotalContSalaries']),
    totalAdminSalaries: readNum(root, ['totalAdminSalaries', 'TotalAdminSalaries']),
    totalExpenseUsd: readNum(root, ['totalExpenseUSD', 'TotalExpenseUSD']),
    totalRevenueUsd: readNum(root, ['totalRevenueUSD', 'TotalRevenueUSD']),
    schoolCount: readNum(root, ['schoolCount', 'SchoolCount']),
    totalSchoolCount: readNum(root, ['totalSchoolCount', 'TotalSchoolCount']),
    submittedSchoolCount: readNum(root, [
      'submitedSchoolCount',
      'SubmitedSchoolCount',
      'submittedSchoolCount',
      'SubmittedSchoolCount',
    ]),
    schoolBands: parseBandList(root, ['schoolBandDtos', 'SchoolBandDtos']),
    tuitionBands: parseBandList(root, ['tuitionBandDtos', 'TuitionBandDtos']),
    parentTuitionBands: parseBandList(root, ['parentTuitionBandDtos', 'ParentTuitionBandDtos']),
    regionSchoolStatistics: parseRegionSchoolStatisticsList(root, [
      'regionSchoolStatistics',
      'RegionSchoolStatistics',
    ]),
    schoolSizeCrossTab: parseGovernorateBandCrossTabList(root, [
      'schoolSizeCrossTab',
      'SchoolSizeCrossTab',
      'governorateBandCrossTab',
      'GovernorateBandCrossTab',
    ]),
    tuitionCrossTab: parseGovernorateBandCrossTabList(root, [
      'tuitionCrossTab',
      'TuitionCrossTab',
      'governorateTuitionBandCrossTab',
      'GovernorateTuitionBandCrossTab',
    ]),
    parentTuitionCrossTab: parseGovernorateBandCrossTabList(root, [
      'parentTuitionCrossTab',
      'ParentTuitionCrossTab',
      'governorateParentTuitionBandCrossTab',
      'GovernorateParentTuitionBandCrossTab',
    ]),
    schools,
  };
}

export type RegionalComparisonsQueryParams = {
  year: number;
  governorateIds?: string[];
  cityIds?: string[];
  areaIds?: string[];
  sorting?: string;
  skipCount?: number;
  maxResultCount?: number;
};

function appendIntArray(q: URLSearchParams, paramName: string, ids: string[] | undefined): void {
  for (const id of ids ?? []) {
    const t = id.trim();
    if (!t) continue;
    const n = Number(t);
    if (Number.isFinite(n)) q.append(paramName, String(n));
  }
}

export const regionalComparisonsApi = {
  getReport: async (params: RegionalComparisonsQueryParams): Promise<RegionalComparisonsReportDto> => {
    const q = new URLSearchParams();
    q.set('Year', String(params.year));
    appendIntArray(q, 'GovernerateIds', params.governorateIds);
    appendIntArray(q, 'CityIds', params.cityIds);
    appendIntArray(q, 'AreaIds', params.areaIds);
    if (params.sorting != null && params.sorting !== '') q.set('Sorting', params.sorting);
    q.set('SkipCount', String(params.skipCount ?? 1));
    q.set('MaxResultCount', String(params.maxResultCount ?? 1));

    const raw = await httpClient.get<unknown>(`${PREFIX}?${q.toString()}`);
    const mapped = mapRegionalComparisonsReport(raw);
    if (!mapped) {
      throw new Error('Regional comparisons response could not be read (empty or invalid payload).');
    }
    return mapped;
  },
};
